(() => {
  const path = window.location.pathname;

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const parseMoney = (text) => {
    const match = String(text || '').match(/£\s*([\d,]+(?:\.\d{1,2})?)/);
    return match ? Number(match[1].replace(/,/g, '')) : null;
  };

  const money = (value) => new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(value);

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };

  const productRoot = () =>
    document.querySelector('.productView-details') ||
    document.querySelector('.productView') ||
    document.querySelector('[data-section-type="product"]') ||
    document.body;

  function getBasePrice() {
    const priceRoot = document.querySelector('.productView-price');
    if (!priceRoot) return null;

    const preferredSelectors = [
      '.price-item--sale',
      '.price__sale .price-item--sale',
      '[data-product-price]',
      '.price-item--regular'
    ];

    for (const selector of preferredSelectors) {
      for (const element of priceRoot.querySelectorAll(selector)) {
        if (!isVisible(element)) continue;
        const value = parseMoney(element.textContent);
        if (value !== null) return value;
      }
    }

    const values = [...priceRoot.querySelectorAll('*')]
      .filter((element) => element.children.length === 0 && isVisible(element))
      .map((element) => parseMoney(element.textContent))
      .filter((value) => value !== null && value > 0);

    return values.length ? Math.min(...values) : parseMoney(priceRoot.textContent);
  }

  function findSurchargeNode(root) {
    const pattern = /Selections will add\s*£\s*[\d,.]+\s*to the price/i;
    return [...root.querySelectorAll('div,p,span')]
      .filter((element) => pattern.test(normalize(element.textContent)))
      .sort((a, b) => normalize(a.textContent).length - normalize(b.textContent).length)[0] || null;
  }

  function getSurcharge(node) {
    if (!node) return null;
    const match = normalize(node.textContent).match(
      /Selections will add\s*£\s*([\d,]+(?:\.\d{1,2})?)\s*to the price/i
    );
    return match ? Number(match[1].replace(/,/g, '')) : null;
  }

  function updateConfiguredTotal() {
    if (!path.startsWith('/products/')) return;

    const root = productRoot();
    const surchargeNode = findSurchargeNode(root);
    const basePrice = getBasePrice();
    const surcharge = getSurcharge(surchargeNode);

    if (!surchargeNode || basePrice === null || surcharge === null) return;

    let summary = root.querySelector('.gmc-configured-price-summary-v2');
    if (!summary) {
      summary = document.createElement('div');
      summary.className = 'gmc-configured-price-summary-v2';
      surchargeNode.insertAdjacentElement('afterend', summary);
    }

    const total = basePrice + surcharge;
    const markup =
      '<div class="gmc-configured-label">Configured total before checkout</div>' +
      '<div class="gmc-configured-total">' + money(total) + '</div>' +
      '<div class="gmc-configured-breakdown">Base product ' + money(basePrice) +
      ' + selected options/services ' + money(surcharge) + '</div>';

    if (summary.innerHTML !== markup) summary.innerHTML = markup;
    surchargeNode.style.display = 'none';
  }

  function hideClaim(pattern) {
    const root = productRoot();
    const candidates = [...root.querySelectorAll('li,div,p,span')]
      .filter((element) => {
        const text = normalize(element.textContent);
        return pattern.test(text) && text.length < 320;
      })
      .sort((a, b) => normalize(a.textContent).length - normalize(b.textContent).length);

    const target = candidates[0];
    if (!target) return;

    const row = target.closest('li') || target;
    row.style.display = 'none';
  }

  function hideRiskyClaims() {
    if (!path.startsWith('/products/')) return;

    hideClaim(/other people want this/i);
    hideClaim(/people have this in their cart right now/i);
    hideClaim(/24\/7 live chat/i);
    hideClaim(/free accessory (?:kit|pack) worth £150/i);
  }

  function replaceStairsWithContactNote() {
    if (!path.startsWith('/products/')) return;

    const root = productRoot();

    for (const select of root.querySelectorAll('select')) {
      const options = [...select.options];
      const isStairsSelect = options.some((option) => {
        const text = normalize(option.textContent);
        return /^Stairs(?:\s*(?:\([^)]*\)|[-–—].*))?$/i.test(text);
      });

      if (!isStairsSelect) continue;

      const placeholder = options.find((option) =>
        /please\s*select/i.test(normalize(option.textContent)) || option.value === ''
      );

      if (!select.dataset.gmcStairsResetV4) {
        select.dataset.gmcStairsResetV4 = '1';

        if (placeholder && select.value !== placeholder.value) {
          select.value = placeholder.value;
          placeholder.selected = true;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      let field = select;
      let node = select.parentElement;

      while (node && node !== root) {
        const selectCount = node.querySelectorAll('select').length;
        const text = normalize(node.textContent);

        if (selectCount !== 1 || text.length > 500) break;

        if (node.querySelector('button[type="submit"], .product-form__submit, [name="add"]')) break;

        field = node;
        node = node.parentElement;
      }

      root.querySelectorAll('label,legend').forEach((label) => {
        const text = normalize(label.textContent);
        const belongsToSelect = Boolean(select.id) && label.htmlFor === select.id;

        if (belongsToSelect || /^Stairs(?:\s+Option)?$/i.test(text)) {
          label.style.setProperty('display', 'none', 'important');
        }
      });

      const oldNote = root.querySelector('#gmc-stairs-contact-note-v3');
      if (oldNote) oldNote.remove();

      let note = root.querySelector('#gmc-stairs-contact-note-v4');
      if (!note) {
        note = document.createElement('div');
        note.id = 'gmc-stairs-contact-note-v4';
        note.className = 'gmc-stairs-contact-note-v3';
        note.innerHTML =
          '<strong>Stairs / restricted access:</strong> Please contact us before ordering so access requirements and any applicable charge can be confirmed.';
        note.style.border = '1px solid #e5e5e5';
        note.style.padding = '12px';
        note.style.margin = '12px 0';
        note.style.lineHeight = '1.45';
        field.insertAdjacentElement('beforebegin', note);
      }

      select.setAttribute('disabled', 'disabled');
      select.style.setProperty('display', 'none', 'important');

      if (field !== select) {
        field.style.setProperty('display', 'none', 'important');
      }
    }
  }

  function hideStickyAddToCart() {
    if (!path.startsWith('/products/')) return;
    document.querySelectorAll('.productView-stickyCart,[data-sticky-add-to-cart]').forEach((element) => {
      element.style.display = 'none';
    });
  }

  function cartSafety() {
    if (path !== '/cart' && path !== '/cart/') return;

    document.querySelectorAll('.cart-countdown').forEach((element) => {
      element.style.display = 'none';
    });

    if (document.getElementById('gmc-cart-configuration-note-v2')) return;

    const checkout = [...document.querySelectorAll('button,a')].find((element) =>
      /proceed to checkout|^checkout$/i.test(normalize(element.textContent))
    );

    if (!checkout || !checkout.parentElement) return;

    const note = document.createElement('p');
    note.id = 'gmc-cart-configuration-note-v2';
    note.className = 'gmc-cart-note-v2';
    note.textContent = 'Your cart total includes the product configuration and any delivery or installation option added on the product page. Please review the total before checkout.';
    checkout.parentElement.insertBefore(note, checkout);
  }

  function fixTableTopStandVariantPicker() {
    if (path !== '/products/table-top-stands') return;

    document.querySelectorAll('variant-selects[data-product="14857208856952"]').forEach((picker) => {
      const selects = [...picker.querySelectorAll('select')];
      const data = picker.querySelector('script[type="application/json"]');

      if (selects.length < 2 || !data) return;

      let variants;
      try {
        variants = JSON.parse(data.textContent);
      } catch (error) {
        return;
      }

      const availableVariants = variants.filter((variant) => variant.available);
      const standSelect = selects[0];
      const finishSelect = selects[1];
      const validStandTypes = new Set(availableVariants.map((variant) => variant.option1));

      [...standSelect.options].forEach((option) => {
        option.disabled = !validStandTypes.has(option.value);
      });

      const selectedVariant = availableVariants.find((variant) =>
        variant.option1 === standSelect.value &&
        variant.option2 === finishSelect.value
      );

      if (selectedVariant) return;

      const compatibleVariant = availableVariants.find((variant) =>
        variant.option1 === standSelect.value
      );

      if (!compatibleVariant || finishSelect.value === compatibleVariant.option2) return;

      finishSelect.value = compatibleVariant.option2;
      finishSelect.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  let queued = false;

  function runAll() {
    queued = false;

    if (path.startsWith('/products/')) {
      fixTableTopStandVariantPicker();
      hideRiskyClaims();
      hideStickyAddToCart();
      replaceStairsWithContactNote();
      updateConfiguredTotal();
    }

    cartSafety();
  }

  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(runAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }

  document.addEventListener('change', schedule, true);

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true
  });
})();
